import React from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    IconButton,
    Typography
} from "@mui/material"
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material"
import { t } from "@lingui/macro"
import { ErrorResponse, Loading, RestResponse } from "@sinossi/mates-react-library"
import { 
    GeographicPermission, 
    GeographicPermissionRequest, 
    GeographicPermissionType 
} from "models"
import { GeographicPermissionService } from "services/geographic-permission.service"
import { MatesComponent, MatesComponentState } from "components/mates-component"

interface GeographicPermissionManagerProps {
    accountId: string
    onClose?: () => void
}

interface GeographicPermissionManagerState extends MatesComponentState {
    permissions: GeographicPermission[]
    loading: boolean
    showAddForm: boolean
    newPermission: GeographicPermissionRequest
}

export default class GeographicPermissionManager extends React.Component<
    GeographicPermissionManagerProps,
    GeographicPermissionManagerState
> {
    private readonly matesComponent: MatesComponent<
        any,
        GeographicPermissionManagerState
    > = new MatesComponent(this as any)

    constructor(props: GeographicPermissionManagerProps) {
        super(props)
        this.state = {
            ...this.matesComponent.setupState(),
            permissions: [],
            loading: true,
            showAddForm: false,
            newPermission: {
                permissionType: GeographicPermissionType.REGION,
                region: "",
                province: "",
                city: ""
            }
        }
    }

    componentDidMount() {
        this.loadPermissions()
    }

    private loadPermissions = () => {
        this.setState({ loading: true })
        GeographicPermissionService.getPermissions(
            this.props.accountId,
            this.onPermissionsLoaded,
            this.matesComponent.onException
        )
    }

    private onPermissionsLoaded = (response: RestResponse<GeographicPermission[], ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState({
            permissions: response.data || [],
            loading: false
        })
    }

    private removePermission = (permissionId: string) => {
        this.setState({ loading: true })
        GeographicPermissionService.removePermission(
            this.props.accountId,
            permissionId,
            this.onPermissionRemoved,
            this.matesComponent.onException
        )
    }

    private onPermissionRemoved = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            this.setState({ loading: false })
            return
        }

        this.loadPermissions()
    }

    private addPermission = () => {
        const { newPermission } = this.state
        
        // Validazione
        if (newPermission.permissionType === GeographicPermissionType.REGION && !newPermission.region) {
            return
        }
        if (newPermission.permissionType === GeographicPermissionType.PROVINCE && !newPermission.province) {
            return
        }
        if (newPermission.permissionType === GeographicPermissionType.CITY && !newPermission.city) {
            return
        }

        this.setState({ loading: true })
        GeographicPermissionService.addPermission(
            this.props.accountId,
            newPermission,
            this.onPermissionAdded,
            this.matesComponent.onException
        )
    }

    private onPermissionAdded = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            this.setState({ loading: false })
            return
        }

        this.setState({
            showAddForm: false,
            newPermission: {
                permissionType: GeographicPermissionType.REGION,
                region: "",
                province: "",
                city: ""
            }
        })
        this.loadPermissions()
    }

    private getPermissionLabel = (permission: GeographicPermission): string => {
        switch (permission.permissionType) {
            case GeographicPermissionType.REGION:
                return `Regione: ${permission.region}`
            case GeographicPermissionType.PROVINCE:
                return `Provincia: ${permission.province}`
            case GeographicPermissionType.CITY:
                return `Città: ${permission.city}`
            default:
                return ""
        }
    }

    private getPermissionColor = (type: GeographicPermissionType): "primary" | "secondary" | "success" => {
        switch (type) {
            case GeographicPermissionType.REGION:
                return "primary"
            case GeographicPermissionType.PROVINCE:
                return "secondary"
            case GeographicPermissionType.CITY:
                return "success"
            default:
                return "primary"
        }
    }

    render() {
        const { permissions, loading, showAddForm, newPermission } = this.state

        return (
            <Card>
                <Loading show={loading} />
                <CardHeader
                    title={t({ id: "geographic-permissions.title", message: "Permessi Geografici" })}
                    action={
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => this.setState({ showAddForm: !showAddForm })}
                            variant="outlined"
                            size="small"
                        >
                            {t({ id: "geographic-permissions.add-single", message: "Aggiungi" })}
                        </Button>
                    }
                />
                <CardContent>
                    {/* Lista permessi esistenti */}
                    <Box sx={{ mb: 2 }}>
                        {permissions.length === 0 ? (
                            <Typography color="text.secondary">
                                {t({ id: "geographic-permissions.no-permissions", message: "Nessun permesso geografico assegnato" })}
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {permissions.map((permission) => (
                                    <Chip
                                        key={permission.id}
                                        label={this.getPermissionLabel(permission)}
                                        color={this.getPermissionColor(permission.permissionType)}
                                        onDelete={() => this.removePermission(permission.id)}
                                        deleteIcon={<DeleteIcon />}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Form per aggiungere nuovo permesso */}
                    {showAddForm && (
                        <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t({ id: "geographic-permissions.add-new", message: "Aggiungi Nuovo Permesso" })}
                            </Typography>
                            
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <select
                                    value={newPermission.permissionType}
                                    onChange={(e) => this.setState({
                                        newPermission: {
                                            ...newPermission,
                                            permissionType: e.target.value as GeographicPermissionType,
                                            region: "",
                                            province: "",
                                            city: ""
                                        }
                                    })}
                                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                                >
                                    <option value={GeographicPermissionType.REGION}>Regione</option>
                                    <option value={GeographicPermissionType.PROVINCE}>Provincia</option>
                                    <option value={GeographicPermissionType.CITY}>Città</option>
                                </select>

                                {newPermission.permissionType === GeographicPermissionType.REGION && (
                                    <input
                                        type="text"
                                        placeholder="Nome regione (es. Lombardia)"
                                        value={newPermission.region}
                                        onChange={(e) => this.setState({
                                            newPermission: { ...newPermission, region: e.target.value }
                                        })}
                                        style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                                    />
                                )}

                                {newPermission.permissionType === GeographicPermissionType.PROVINCE && (
                                    <input
                                        type="text"
                                        placeholder="Nome provincia (es. Milano)"
                                        value={newPermission.province}
                                        onChange={(e) => this.setState({
                                            newPermission: { ...newPermission, province: e.target.value }
                                        })}
                                        style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                                    />
                                )}

                                {newPermission.permissionType === GeographicPermissionType.CITY && (
                                    <input
                                        type="text"
                                        placeholder="Nome città (es. Roma)"
                                        value={newPermission.city}
                                        onChange={(e) => this.setState({
                                            newPermission: { ...newPermission, city: e.target.value }
                                        })}
                                        style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                                    />
                                )}

                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={this.addPermission}
                                        size="small"
                                    >
                                        {t({ id: "geographic-permissions.save", message: "Salva" })}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => this.setState({ showAddForm: false })}
                                        size="small"
                                    >
                                        {t({ id: "geographic-permissions.cancel", message: "Annulla" })}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        )
    }
}
